// Detects outdated browsers (common on Win7 / old tablets) and shows a one-time
// dismissible banner with an upgrade recommendation. Small PWA footprint.
//
// Triggers for:
//  - Chrome/Chromium <= 109
//  - Firefox <= 102
//  - Safari <= 14
//  - Any version of Internet Explorer
//  - EDGE <= 91 (Legacy Edge)
//
// Banner dismissed with localStorage('browser_too_old_ack' = UA-hash) so it
// doesn't nag the same browser forever; it will re-show if user upgrades partially.
import React, { useEffect, useState } from 'react';
import { tr } from '@/i18n/tr';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';

const MIN = { chrome: 110, firefox: 103, safari: 15, edge: 110 };

function detectOutdated() {
  const ua = navigator.userAgent || '';
  // IE
  if (/MSIE |Trident\//.test(ua)) return { outdated: true, browser: 'Internet Explorer', ver: 'any' };

  // Edge legacy
  const edgeLegacy = ua.match(/Edge\/(\d+)/);
  if (edgeLegacy) return { outdated: true, browser: 'Microsoft Edge (Legacy)', ver: edgeLegacy[1] };

  // Chromium Edge / Chrome
  const chrome = ua.match(/Chrome\/(\d+)/);
  if (chrome) {
    const v = parseInt(chrome[1], 10);
    const edge = ua.match(/Edg\/(\d+)/);
    if (edge) {
      const ev = parseInt(edge[1], 10);
      if (ev < MIN.edge) return { outdated: true, browser: 'Microsoft Edge', ver: ev };
    } else if (v < MIN.chrome) return { outdated: true, browser: 'Chrome', ver: v };
  }

  // Firefox
  const fx = ua.match(/Firefox\/(\d+)/);
  if (fx) {
    const v = parseInt(fx[1], 10);
    if (v < MIN.firefox) return { outdated: true, browser: 'Firefox', ver: v };
  }

  // Safari (Mac/iOS) — exclude Chrome/Android
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua)) {
    const s = ua.match(/Version\/(\d+)/);
    if (s) {
      const v = parseInt(s[1], 10);
      if (v < MIN.safari) return { outdated: true, browser: 'Safari', ver: v };
    }
  }
  return { outdated: false };
}

export default function BrowserWarning() {
  const [state, setState] = useState({ show: false, browser: '', ver: '' });

  useEffect(() => {
    const { outdated, browser, ver } = detectOutdated();
    if (!outdated) return;
    const acked = localStorage.getItem('browser_too_old_ack');
    const sig = `${browser}-${ver}`;
    if (acked === sig) return;
    setState({ show: true, browser, ver, sig });
  }, []);

  if (!state.show) return null;

  const dismiss = () => {
    localStorage.setItem('browser_too_old_ack', state.sig);
    setState(s => ({ ...s, show: false }));
  };

  const links = [
    { name: tr('x_edf4de14'), href: 'https://www.google.com/chrome/' },
    { name: tr('x_c9dc4ffd'), href: 'https://www.mozilla.org/firefox/enterprise/' },
    { name: 'Microsoft Edge', href: 'https://www.microsoft.com/edge' },
  ];

  return (
    <div className="fixed top-0 inset-x-0 z-[300] bg-amber-600 text-white shadow-lg border-b border-amber-800" data-testid="browser-too-old-banner">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold">
            {tr('x_e468fa4f')} {state.browser} {state.ver}
          </div>
          <div className="text-amber-100 mt-0.5 text-xs leading-relaxed">
            {tr('x_ffcd6690')}
            <br/>
            <span className="text-white">Some features (offline POS, PWA install, barcode scanner) may <b>not work</b>. Please upgrade to:</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {links.map(l => (
              <a key={l.name} href={l.href} target="_blank" rel="noreferrer"
                className="bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-full px-3 py-1 transition flex items-center gap-1"
                data-testid={`browser-upgrade-${l.name.split(' ')[0].toLowerCase()}`}>
                {l.name} <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
        <button onClick={dismiss} className="text-amber-100 hover:text-white shrink-0" title="dismiss" data-testid="browser-warning-dismiss">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
